import React, { useState } from 'react';
import { Calendar, Activity, AlertCircle, Download, User, ArrowRight, Info, ShoppingBag } from 'lucide-react';
import PressureDistributionMap from './foot-health/PressureDistributionMap';
import FootConditionAnalytics from './foot-health/FootConditionAnalytics';
import FootwearAnalysis from './foot-health/FootwearAnalysis';
import RecommendationEngine from './foot-health/RecommendationEngine';

// Types for foot health monitoring
export interface FootRegion {
  id: string;
  name: string;
  normalPressureRange: [number, number]; // Min and max normal pressure (0-100)
}

export interface FootPressureData {
  id: string;
  timestamp: Date;
  regions: {
    [regionId: string]: {
      averagePressure: number;
      peakPressure: number;
      contactArea: number;
      pressurePoints: Array<{ x: number; y: number; pressure: number }>;
    };
  };
  aggregateMetrics: {
    symmetryIndex: number; // 0-100 scale, 100 being perfect symmetry
    medialLateralBalance: number; // -100 to 100, 0 is balanced, negative is medial, positive is lateral
    anteriorPosteriorBalance: number; // -100 to 100, 0 is balanced, negative is posterior, positive is anterior
    weightDistribution: {
      forefoot: number; // percentage
      midfoot: number; // percentage
      heel: number; // percentage
    };
  };
  alertFlags: {
    highPressurePoints: boolean;
    asymmetry: boolean;
    abnormalDistribution: boolean;
  };
}

export interface FootHealthAssessment {
  id: string;
  date: Date;
  patientId: string;
  patientName: string;
  leftFootData: FootPressureData;
  rightFootData: FootPressureData;
  overallAssessment: {
    plantarFasciitisRisk: number; // 0-100, higher means more risk
    diabeticNeuropathyIndicators: number; // 0-100
    improperFootwearSigns: number; // 0-100
  };
  recommendedActions: Array<{
    id: string;
    type: 'FOOTWEAR' | 'EXERCISE' | 'MEDICAL' | 'LIFESTYLE';
    title: string;
    description: string;
    urgency: 'LOW' | 'MEDIUM' | 'HIGH';
    completed: boolean;
  }>;
  notes: string;
}

// Define standard foot regions
export const FOOT_REGIONS: FootRegion[] = [
  { id: 'heel', name: 'Heel', normalPressureRange: [40, 70] },
  { id: 'lateral_midfoot', name: 'Lateral Midfoot', normalPressureRange: [20, 45] },
  { id: 'medial_midfoot', name: 'Medial Midfoot (Arch)', normalPressureRange: [10, 30] },
  { id: 'lateral_forefoot', name: 'Lateral Forefoot', normalPressureRange: [30, 60] },
  { id: 'medial_forefoot', name: 'Medial Forefoot', normalPressureRange: [35, 65] },
  { id: 'hallux', name: 'Big Toe (Hallux)', normalPressureRange: [40, 70] },
  { id: 'lesser_toes', name: 'Lesser Toes', normalPressureRange: [25, 55] }
];

// Generate mock data for testing
const generateMockFootHealthAssessments = (): FootHealthAssessment[] => {
  const assessments: FootHealthAssessment[] = [];
  const today = new Date();
  
  // Create a baseline assessment (normal foot health) from 3 months ago
  const baselineDate = new Date(today);
  baselineDate.setMonth(today.getMonth() - 3);
  
  // Helper to generate normal foot pressure data
  const generateNormalFootData = (id: string, timestamp: Date, side: 'left' | 'right'): FootPressureData => {
    const regions: { [regionId: string]: any } = {};
    
    FOOT_REGIONS.forEach(region => {
      const midRange = (region.normalPressureRange[0] + region.normalPressureRange[1]) / 2;
      const variation = (region.normalPressureRange[1] - region.normalPressureRange[0]) * 0.3;
      
      // Generate slightly different but normal values for each region
      const avgPressure = midRange + (Math.random() * variation * 2 - variation);
      
      // Generate random pressure points for the region
      const numPoints = 10 + Math.floor(Math.random() * 10);
      const pressurePoints = [];
      
      // Base coordinates for visualizing different foot regions
      let baseX, baseY;
      switch(region.id) {
        case 'heel':
          baseX = 50; baseY = 90;
          break;
        case 'lateral_midfoot':
          baseX = side === 'left' ? 35 : 65; baseY = 60;
          break;
        case 'medial_midfoot':
          baseX = side === 'left' ? 65 : 35; baseY = 60;
          break;
        case 'lateral_forefoot':
          baseX = side === 'left' ? 35 : 65; baseY = 30;
          break;
        case 'medial_forefoot':
          baseX = side === 'left' ? 65 : 35; baseY = 30;
          break;
        case 'hallux':
          baseX = side === 'left' ? 65 : 35; baseY = 10;
          break;
        case 'lesser_toes':
          baseX = side === 'left' ? 40 : 60; baseY = 10;
          break;
        default:
          baseX = 50; baseY = 50;
      }
      
      for (let i = 0; i < numPoints; i++) {
        pressurePoints.push({
          x: baseX + (Math.random() * 20 - 10),
          y: baseY + (Math.random() * 20 - 10),
          pressure: avgPressure + (Math.random() * 10 - 5)
        });
      }
      
      regions[region.id] = {
        averagePressure: avgPressure,
        peakPressure: avgPressure + Math.random() * 15,
        contactArea: 40 + Math.random() * 20,
        pressurePoints
      };
    });
    
    return {
      id,
      timestamp,
      regions,
      aggregateMetrics: {
        symmetryIndex: 90 + Math.random() * 10,
        medialLateralBalance: Math.random() * 10 - 5,
        anteriorPosteriorBalance: Math.random() * 10 - 5,
        weightDistribution: {
          forefoot: 35 + Math.random() * 10,
          midfoot: 20 + Math.random() * 5,
          heel: 45 + Math.random() * 10
        }
      },
      alertFlags: {
        highPressurePoints: false,
        asymmetry: false,
        abnormalDistribution: false
      }
    };
  };
  
  // Add baseline assessment
  assessments.push({
    id: 'assessment-baseline',
    date: baselineDate,
    patientId: 'patient-123',
    patientName: 'Emma Martinez',
    leftFootData: generateNormalFootData('left-foot-baseline', baselineDate, 'left'),
    rightFootData: generateNormalFootData('right-foot-baseline', baselineDate, 'right'),
    overallAssessment: {
      plantarFasciitisRisk: 15,
      diabeticNeuropathyIndicators: 10,
      improperFootwearSigns: 20
    },
    recommendedActions: [],
    notes: 'Baseline assessment. Normal foot pressure distribution observed.'
  });
  
  // Generate monthly assessments with gradually developing plantar fasciitis in right foot
  for (let i = 2; i >= 0; i--) {
    const assessmentDate = new Date(today);
    assessmentDate.setMonth(today.getMonth() - i);
    
    // Left foot remains relatively normal
    const leftFootData = generateNormalFootData(`left-foot-${i}`, assessmentDate, 'left');
    
    // Right foot shows increasing signs of plantar fasciitis
    const rightFootData = generateNormalFootData(`right-foot-${i}`, assessmentDate, 'right');
    
    // Modify right foot data to show plantar fasciitis development
    // Increased heel pressure
    const heelPressureIncrease = (2 - i) * 10; // 0, 10, 20 increase
    rightFootData.regions.heel.averagePressure += heelPressureIncrease;
    rightFootData.regions.heel.peakPressure += heelPressureIncrease * 1.5;
    
    // Update pressure points for heel
    rightFootData.regions.heel.pressurePoints.forEach(point => {
      point.pressure += heelPressureIncrease * (0.8 + Math.random() * 0.4);
    });
    
    // Decreased medial midfoot (arch) pressure as arch collapses less weight there
    const archPressureDecrease = (2 - i) * 5; // 0, 5, 10 decrease
    rightFootData.regions.medial_midfoot.averagePressure = 
      Math.max(5, rightFootData.regions.medial_midfoot.averagePressure - archPressureDecrease);
    rightFootData.regions.medial_midfoot.peakPressure = 
      Math.max(10, rightFootData.regions.medial_midfoot.peakPressure - archPressureDecrease);
    
    // Update aggregate metrics
    rightFootData.aggregateMetrics.symmetryIndex = Math.max(70, 90 - (2 - i) * 10);
    rightFootData.aggregateMetrics.medialLateralBalance += (2 - i) * 5; // More lateral weight
    
    // Set alert flags as condition develops
    if (i <= 1) {
      rightFootData.alertFlags.asymmetry = true;
    }
    if (i === 0) {
      rightFootData.alertFlags.highPressurePoints = true;
      rightFootData.alertFlags.abnormalDistribution = true;
    }
    
    // Calculate plantar fasciitis risk based on progression
    const pfRisk = 15 + (2 - i) * 25; // 15, 40, 65
    
    // Generate recommendations based on risk level
    const recommendations = [];
    if (pfRisk > 30) {
      recommendations.push({
        id: `rec-footwear-${i}`,
        type: 'FOOTWEAR' as const,
        title: 'Consider supportive footwear',
        description: 'Shoes with good arch support and cushioned heels may help reduce heel pain.',
        urgency: 'MEDIUM' as const,
        completed: false
      });
    }
    if (pfRisk > 50) {
      recommendations.push({
        id: `rec-exercise-${i}`,
        type: 'EXERCISE' as const,
        title: 'Foot stretching exercises',
        description: 'Daily calf stretches and plantar fascia stretches can help reduce pain and improve flexibility.',
        urgency: 'MEDIUM' as const,
        completed: false
      });
      recommendations.push({
        id: `rec-medical-${i}`,
        type: 'MEDICAL' as const,
        title: 'Consult with podiatrist',
        description: 'Your pressure patterns suggest early signs of plantar fasciitis. A professional evaluation is recommended.',
        urgency: 'HIGH' as const,
        completed: false
      });
    }
    
    assessments.push({
      id: `assessment-${i}`,
      date: assessmentDate,
      patientId: 'patient-123',
      patientName: 'Emma Martinez',
      leftFootData,
      rightFootData,
      overallAssessment: {
        plantarFasciitisRisk: pfRisk,
        diabeticNeuropathyIndicators: 10 + Math.random() * 5,
        improperFootwearSigns: 20 + (2 - i) * 10
      },
      recommendedActions: recommendations,
      notes: i === 0 
        ? 'Significant changes in right foot pressure distribution. Signs consistent with developing plantar fasciitis.'
        : i === 1 
          ? 'Moderate changes in right heel pressure. Early monitoring recommended.' 
          : 'Slight deviation from baseline in right foot pressure patterns.'
    });
  }
  
  return assessments;
};

const mockAssessments = generateMockFootHealthAssessments();

const FootHealthMonitoring: React.FC = () => {
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>(mockAssessments[0].id);
  const [activeTab, setActiveTab] = useState<'pressure' | 'conditions' | 'footwear' | 'recommendations'>('pressure');
  const [compareWithBaseline, setCompareWithBaseline] = useState<boolean>(true);
  
  // Get current assessment and baseline assessment
  const currentAssessment = mockAssessments.find(a => a.id === selectedAssessmentId) || mockAssessments[0];
  const baselineAssessment = mockAssessments.find(a => a.id === 'assessment-baseline');
  
  // Handle tab change
  const handleTabChange = (tab: 'pressure' | 'conditions' | 'footwear' | 'recommendations') => {
    setActiveTab(tab);
  };
  
  // Handle assessment selection
  const handleAssessmentChange = (assessmentId: string) => {
    setSelectedAssessmentId(assessmentId);
  };
  
  // Toggle baseline comparison
  const handleCompareToggle = () => {
    setCompareWithBaseline(!compareWithBaseline);
  };
  
  // Format date for display
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Calculate risk level class
  const getRiskLevelClass = (riskScore: number): string => {
    if (riskScore >= 60) return 'text-red-600 bg-red-50';
    if (riskScore >= 30) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };
  
  // Render appropriate component based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'pressure':
        return (
          <PressureDistributionMap
            assessment={currentAssessment}
            baselineAssessment={compareWithBaseline ? baselineAssessment : undefined}
          />
        );
      case 'conditions':
        return (
          <FootConditionAnalytics
            assessment={currentAssessment}
            baselineAssessment={compareWithBaseline ? baselineAssessment : undefined}
            allAssessments={mockAssessments}
          />
        );
      case 'footwear':
        return (
          <FootwearAnalysis
            assessment={currentAssessment}
          />
        );
      case 'recommendations':
        return (
          <RecommendationEngine
            assessment={currentAssessment}
          />
        );
      default:
        return (
          <PressureDistributionMap
            assessment={currentAssessment}
            baselineAssessment={compareWithBaseline ? baselineAssessment : undefined}
          />
        );
    }
  };
  
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Foot Health Monitoring</h2>
          <p className="text-gray-600">
            Tracking foot pressure distributions and potential health concerns
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {}}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50"
          >
            <Download size={16} className="mr-2" />
            Export Report
          </button>
          <button
            onClick={handleCompareToggle}
            className={`flex items-center px-3 py-2 ${
              compareWithBaseline 
                ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                : 'bg-white border border-gray-300 text-gray-700'
            } rounded-md text-sm hover:bg-blue-50`}
          >
            <Activity size={16} className="mr-2" />
            {compareWithBaseline ? 'Comparing with Baseline' : 'Compare with Baseline'}
          </button>
        </div>
      </div>
      
      {/* Risk summary */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="font-medium">Current Assessment Summary</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col">
              <div className="flex items-center mb-2">
                <User className="text-blue-500 mr-2" size={20} />
                <span className="font-medium">Patient Information</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium">{currentAssessment.patientName}</p>
                <p className="text-sm text-gray-600">ID: {currentAssessment.patientId}</p>
                <p className="text-sm text-gray-600">
                  Assessment Date: {formatDate(currentAssessment.date)}
                </p>
                {currentAssessment.id !== 'assessment-baseline' && (
                  <p className="text-sm text-gray-600">
                    Days Since Baseline: {Math.round(
                      (currentAssessment.date.getTime() - baselineAssessment!.date.getTime()) / 
                      (1000 * 60 * 60 * 24)
                    )}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex flex-col">
              <div className="flex items-center mb-2">
                <AlertCircle className="text-yellow-500 mr-2" size={20} />
                <span className="font-medium">Risk Assessment</span>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div className={`rounded-lg p-3 ${getRiskLevelClass(currentAssessment.overallAssessment.plantarFasciitisRisk)}`}>
                  <div className="flex justify-between">
                    <span className="font-medium">Plantar Fasciitis</span>
                    <span>{currentAssessment.overallAssessment.plantarFasciitisRisk}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div className="h-2.5 rounded-full bg-current"
                      style={{ width: `${currentAssessment.overallAssessment.plantarFasciitisRisk}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className={`rounded-lg p-3 ${getRiskLevelClass(currentAssessment.overallAssessment.diabeticNeuropathyIndicators)}`}>
                  <div className="flex justify-between">
                    <span className="font-medium">Diabetic Neuropathy</span>
                    <span>{currentAssessment.overallAssessment.diabeticNeuropathyIndicators}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div className="h-2.5 rounded-full bg-current"
                      style={{ width: `${currentAssessment.overallAssessment.diabeticNeuropathyIndicators}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col">
              <div className="flex items-center mb-2">
                <ShoppingBag className="text-purple-500 mr-2" size={20} />
                <span className="font-medium">Footwear Assessment</span>
              </div>
              <div className={`rounded-lg p-3 ${getRiskLevelClass(currentAssessment.overallAssessment.improperFootwearSigns)}`}>
                <div className="flex justify-between">
                  <span className="font-medium">Improper Footwear Signs</span>
                  <span>{currentAssessment.overallAssessment.improperFootwearSigns}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                  <div className="h-2.5 rounded-full bg-current"
                    style={{ width: `${currentAssessment.overallAssessment.improperFootwearSigns}%` }}
                  ></div>
                </div>
                
                {currentAssessment.recommendedActions.some(a => a.type === 'FOOTWEAR') && (
                  <div className="mt-3 text-sm">
                    <span className="font-medium">Recommendation:</span> Update footwear with proper support
                  </div>
                )}
              </div>
              
              {currentAssessment.recommendedActions.some(a => a.urgency === 'HIGH') && (
                <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-start">
                  <Info size={16} className="mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Medical consultation recommended based on current assessment</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Assessment selector */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="font-medium">Historical Assessments</h3>
        </div>
        <div className="p-4 flex overflow-x-auto space-x-4">
          {mockAssessments.map(assessment => (
            <button
              key={assessment.id}
              onClick={() => handleAssessmentChange(assessment.id)}
              className={`flex-shrink-0 p-3 rounded-lg border ${
                selectedAssessmentId === assessment.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="whitespace-nowrap font-medium">
                {formatDate(assessment.date)}
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {assessment.id === 'assessment-baseline' ? 'Baseline' : 
                 `PF Risk: ${assessment.overallAssessment.plantarFasciitisRisk}%`}
              </div>
              <div className="mt-2 flex items-center">
                {assessment.id === selectedAssessmentId && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">Selected</span>
                )}
                {assessment.id === 'assessment-baseline' && (
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full">Baseline</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Navigation Tabs */}
      <div className="flex overflow-x-auto mb-6 border-b">
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'pressure'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => handleTabChange('pressure')}
        >
          <Activity className="inline-block mr-2 w-5 h-5" />
          Pressure Distribution
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'conditions'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => handleTabChange('conditions')}
        >
          <AlertCircle className="inline-block mr-2 w-5 h-5" />
          Condition Analysis
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'footwear'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => handleTabChange('footwear')}
        >
          <ShoppingBag className="inline-block mr-2 w-5 h-5" />
          Footwear Analysis
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'recommendations'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => handleTabChange('recommendations')}
        >
          <ArrowRight className="inline-block mr-2 w-5 h-5" />
          Recommendations
        </button>
      </div>
      
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default FootHealthMonitoring; 